import { useMetadata } from '@contexts/org-metadata';
import {
  ArrowCounterClockwiseIcon,
  FileIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import {
  Button,
  Cell,
  Column,
  FileDropzone,
  Modal,
  MultiSelect,
  Row,
  Select,
  TableFrame,
  TableHeader,
  Tooltip,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { ItemStates } from '@utility/constants/common';
import { DateFormat, DateUtility } from '@utility/date';
import CatalogService from '@utility/services/catalog';
import UploadService from '@utility/services/upload';
import { ToastMessage } from '@utility/toast';
import { useEffect, useMemo, useState } from 'react';
import {
  type CatalogFeed,
  useCatalogFeeds,
} from '../../hooks/use-catalog-feeds';
import { flagImages } from '../../legacy-bridge';

type Region = {
  marketplace_region_id: string;
  region_alias?: string;
  name?: string;
};

const FEED_TYPE = 'items-import';
const STATE_OPTIONS = Object.values(ItemStates).map(state => ({
  value: state,
  label: state,
}));

function FeedMessages({ results }: { results: CatalogFeed['results'] }) {
  if (CommonUtility.isValidArray(results?.errors)) {
    return (
      <ol className="sk:m-0 sk:flex sk:max-w-100 sk:flex-col sk:gap-0.5 sk:pl-4">
        {results?.errors?.map((error, index) => (
          <li key={index}>{error.message}</li>
        ))}
      </ol>
    );
  }

  const success = results?.messages?.success ?? '';
  return CommonUtility.isValidHTML(success) ? (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: the import service writes this summary as HTML.
    <div
      className="sk:max-w-100"
      dangerouslySetInnerHTML={{ __html: success }}
    />
  ) : (
    <div className="sk:max-w-100">{success}</div>
  );
}

/** Uploads an item feed (CSV) and lists the recent ones with their outcome. */
export function CatalogFeedModal({
  isOpen,
  onClose,
  regionId,
  isComponent = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  regionId?: string | null;
  isComponent?: boolean;
}) {
  const { regions } = useMetadata() as { regions?: Region[] };
  const regionList = regions ?? [];
  const [files, setFiles] = useState<File[]>([]);
  const [states, setStates] = useState<string[]>([
    isComponent ? ItemStates.component : ItemStates.resolved,
  ]);
  const [pickedRegionId, setPickedRegionId] = useState<string>();
  const [templateLoading, setTemplateLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const feeds = useCatalogFeeds(
    isComponent ? ItemStates.component : undefined,
    isOpen,
  );

  /* The page's region when it has one, else the one picked here (US first). */
  const usRegionId = regionList.find(
    region => region.name === 'US',
  )?.marketplace_region_id;
  const selectedRegionId = regionId || pickedRegionId || usRegionId;

  useEffect(() => {
    if (!isOpen) setFiles([]);
  }, [isOpen]);

  const regionOptions = useMemo(
    () =>
      regionList.map(region => ({
        value: region.marketplace_region_id,
        label: region.region_alias || region.name || '',
        leading: (
          <img
            src={flagImages[(region.name || '').toUpperCase()] ?? flagImages.WW}
            alt=""
            className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-cover"
          />
        ),
      })),
    [regionList],
  );

  const templateName = isComponent
    ? 'component-feed-template'
    : 'catalog-feed-template';

  const downloadTemplate = async (params: Record<string, string>) => {
    try {
      setTemplateLoading(true);
      const csv = await CatalogService.downloadFeedTemplate(params);
      CommonUtility.downloadCSV(csv, templateName);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const upload = async () => {
    const file = files[0];
    if (!file) {
      ToastMessage.error('Please select a file');
      return;
    }
    try {
      setUploading(true);
      const { uploadUrl } = await CatalogService.getCatalogFeedUpload(
        FEED_TYPE,
        { regionId: selectedRegionId, feedType: FEED_TYPE },
      );
      await UploadService.media(uploadUrl, file, file.type);
      ToastMessage.success('File uploaded successfully');
      feeds.refresh();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      size="xl"
      title={`${isComponent ? 'Component' : 'Catalog'} feed`}
      footer={
        <>
          <Button variant="ghost" onPress={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onPress={upload}
            isDisabled={uploading || !files.length}
          >
            Upload
          </Button>
        </>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-4">
        <div className="sk:flex sk:flex-wrap sk:items-end sk:gap-3">
          <div className="sk:flex sk:min-w-64 sk:flex-1 sk:flex-col sk:gap-1">
            <span className="sk:text-xs sk:text-fg-tertiary">
              Product status for the pre-filled template
            </span>
            <MultiSelect
              aria-label="Product status"
              placeholder="Select the product status"
              options={STATE_OPTIONS}
              values={states}
              onChange={setStates}
            />
          </div>
          {regionId ? null : (
            <Select
              aria-label="Region"
              value={selectedRegionId}
              onChange={setPickedRegionId}
              options={regionOptions}
              className="sk:w-56"
            />
          )}
          <Button
            variant="pill"
            isDisabled={templateLoading || !states.length}
            onPress={() =>
              downloadTemplate({
                marketplaceRegionId: selectedRegionId ?? '',
                state: states.join(','),
              })
            }
          >
            Download pre-filled template
          </Button>
          <Button
            variant="pill"
            isDisabled={templateLoading}
            onPress={() =>
              downloadTemplate(
                isComponent ? { state: ItemStates.component } : {},
              )
            }
          >
            Download basic template
          </Button>
        </div>

        <FileDropzone
          value={files}
          onChange={setFiles}
          accept={['.csv']}
          multiple={false}
          label="Feed file"
          hint="One CSV file"
        />

        <div className="sk:flex sk:items-center sk:gap-2">
          <span className="sk:text-sm sk:font-medium sk:text-fg">
            Recent feeds
          </span>
          <Tooltip content="Refresh">
            <Button
              variant="ghost"
              size="sm"
              shape="circle"
              aria-label="Refresh feeds"
              isDisabled={feeds.loading}
              onPress={feeds.refresh}
            >
              <ArrowCounterClockwiseIcon size={12} weight="bold" aria-hidden />
            </Button>
          </Tooltip>
        </div>
        <TableFrame
          aria-label="Recent feeds"
          columnCount={3}
          isLoading={feeds.loading}
          loadingRows={5}
          emptyMessage="No feeds uploaded yet"
          pagination={{
            page: feeds.page,
            pageSize: feeds.limit,
            total: feeds.feeds.length ? feeds.total : 0,
            onPageChange: feeds.setPage,
            onPageSizeChange: feeds.setLimit,
          }}
          header={
            <TableHeader>
              <Column id="created" isRowHeader>
                Uploaded
              </Column>
              <Column id="status">Status</Column>
              <Column id="file" aria-label="File" className="sk:w-32">
                {''}
              </Column>
            </TableHeader>
          }
        >
          {feeds.feeds.map(feed => (
            <Row key={feed._id} id={feed._id}>
              <Cell className="sk:whitespace-nowrap">
                {DateUtility.formatDate(feed.createdAt, DateFormat.dateTime)}
              </Cell>
              <Cell>
                <span className="sk:flex sk:items-center sk:gap-1.5">
                  {CommonUtility.removeUnderscoreAndCapitalize(feed.status)}
                  <Tooltip content={<FeedMessages results={feed.results} />}>
                    <Button
                      variant="ghost"
                      size="sm"
                      shape="circle"
                      aria-label="Feed details"
                    >
                      <InfoIcon size={14} aria-hidden />
                    </Button>
                  </Tooltip>
                </span>
              </Cell>
              <Cell>
                {feed.url ? (
                  <a
                    href={feed.url}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className="sk:inline-flex sk:items-center sk:gap-1.5 sk:text-sm sk:text-primary"
                  >
                    <FileIcon size={16} aria-hidden />
                    View file
                  </a>
                ) : null}
              </Cell>
            </Row>
          ))}
        </TableFrame>
      </div>
    </Modal>
  );
}
